import { createStyles, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Select, Typography } from '@nutrien/cxp-components';
import React from 'react';

import { DeficiencyListModified } from '../../models/models';
import { EnvironmentalCondition } from '../../rxdb/environmentalCondition/queryBuilder';
import { RepairFactor } from '../../rxdb/repairFactor/queryBuilder';
import { ComponentInfo, RiskAssessment } from '../../utilities/enums';

interface Props {
  repairFactorListData: RepairFactor[];
  deficiencyPayload: DeficiencyListModified;
  validateInputs: () => void;
  environmentalConditionListData: EnvironmentalCondition[];
  maxEnvironmentalCondition: number;
  componentAreaLength: number;
  setDeficiencyPayload: React.Dispatch<React.SetStateAction<DeficiencyListModified>>;
  isEditable: boolean;
}

const useStyles = makeStyles(() =>
  createStyles({
    outerGrid: {
      padding: '16px',
      marginTop: '16px',
      marginBottom: '1px',
      borderRadius: '10px ',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      '& .fieldLabel': {
        marginBottom: '8px',
      },
    },
    riskAssesmentGrid: {
      marginBottom: 10,
    },
    requierd: {
      color: '#2AAB3F',
    },
    breakLine: {
      border: '1px solid #FFFFFF0D',
    },
    checkboxLabel: {
      width: '100%',
    },
    conditionRating: {
      display: 'flex',
      marginTop: 10,
      marginBottom: 10,
    },
    firstAutoComplete: {
      marginTop: '0px',
    },
    selectHeight: {
      height: '44px',
    },
    repairFactor: {
      alignSelf: 'flex-start',
    },
  }),
);

const RiskAssessmentPanelForElectrical: React.FC<Props> = ({
  deficiencyPayload,
  repairFactorListData,
  validateInputs,
  maxEnvironmentalCondition,
  environmentalConditionListData,
  componentAreaLength,
  setDeficiencyPayload,
  isEditable,
}) => {
  const classes = useStyles();

  const handleChangeRisk = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { name, value },
    } = event;
    setDeficiencyPayload(prev => {
      return {
        ...prev,
        [name]: Number(value),
        isChanged: true,
      };
    });
    validateInputs();
  };

  return (
    <Grid className={classes.outerGrid} item xs={12} container>
      <Grid container spacing={2}>
        <Grid item container xs={12} alignItems="center">
          <Grid item xs={12} className={classes.riskAssesmentGrid}>
            <Typography variant="h6">Risk Assesment</Typography>
            <Typography variant="caption">
              <span>
                Fields with<span className={classes.requierd}> *</span>are mandatory
              </span>
            </Typography>
            <hr className={`${classes.breakLine} ${classes.checkboxLabel}`} />
          </Grid>
          <Grid item xs={12} className={`${classes.conditionRating} ${classes.repairFactor}`}>
            <Grid item xs={6} className={classes.repairFactor}>
              <Typography variant="subtitle2" className="fieldLabel">
                <span>
                  Repair Factor<span className={classes.requierd}> *</span>
                </span>
              </Typography>
              <Select
                name={ComponentInfo.REPAIR_FACTOR_ID}
                list={repairFactorListData}
                required
                getOptionLabel={list => `${list?.id} - ${list?.name}`}
                getOptionValue={list => list?.id}
                onChange={handleChangeRisk}
                placeholder="Select Repair Factor"
                disabled={!isEditable}
                defaultValue={
                  deficiencyPayload[ComponentInfo.REPAIR_FACTOR_ID]
                    ? deficiencyPayload[ComponentInfo.REPAIR_FACTOR_ID]
                    : ''
                }
                className={`${classes.firstAutoComplete} ${classes.selectHeight}`}
                noMinHeight
              />
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={6}>
              <Typography variant="subtitle2" className="fieldLabel">
                <span>
                  Environmental Condition
                  <span className={classes.requierd}> *</span>
                </span>
              </Typography>
              <Select
                name={RiskAssessment.ENVIRONMENTAL_CONDITION}
                list={environmentalConditionListData}
                getOptionLabel={list => `${list?.riskValue} - ${list?.riskName}`}
                getOptionValue={list => list?.riskValue}
                placeholder="Select Environmental Data"
                value={maxEnvironmentalCondition}
                className={`${classes.firstAutoComplete} ${classes.selectHeight}`}
                noMinHeight
                disabled
              />
              {maxEnvironmentalCondition === -1 && componentAreaLength ? (
                <Typography variant="subtitle2" className="fieldLabel" color="error">
                  Selected component location does not have environmental condition score
                </Typography>
              ) : (
                ''
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default RiskAssessmentPanelForElectrical;
